import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api } from 'common';

/**
 * UserPasswordDialog - Modern password management dialog for admin
 * Allows admin to set password and username for users
 */
export default function UserPasswordDialog({ open, onClose, user, onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  const { adminSetUserPassword, checkUsernameAvailable } = api;

  useEffect(() => {
    if (open && user) {
      setUsername(user.username || '');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setUsernameAvailable(null);
    }
  }, [open, user]);

  // Check username availability on change
  useEffect(() => {
    if (!username || username === user?.username) {
      setUsernameAvailable(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const result = await checkUsernameAvailable(username, user?.id);
        setUsernameAvailable(result.available);
        if (!result.available && result.error) {
          setError(result.error);
        } else {
          setError('');
        }
      } catch (err) {
        setError('Failed to check username availability');
      } finally {
        setCheckingUsername(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, user, checkUsernameAvailable]);

  const validatePassword = () => {
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate
    if (!validatePassword()) return;

    if (username && usernameAvailable === false) {
      setError('Username is not available');
      return;
    }

    setLoading(true);

    try {
      const result = await adminSetUserPassword(user.id, password, username || null);

      if (result.success) {
        setSuccess('Password and login credentials updated successfully!');
        setTimeout(() => {
          if (onSuccess) onSuccess(result.data);
          onClose();
        }, 1500);
      } else {
        setError(result.error || 'Failed to update password');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockIcon />
          <Typography variant="h6" fontWeight={600}>
            Set Login Credentials
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ mt: 3 }}>
        {/* User Info */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: '#F3F4F6',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#667eea',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 18
            }}
          >
            {user.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={600} color="#111827">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="#6B7280">
              {user.email || user.mobile}
            </Typography>
          </Box>
          {user.passwordLoginEnabled && (
            <Chip
              label="Password Login Enabled"
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
            />
          )}
        </Box>

        {/* Username Field */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>
            Username (optional)
          </Typography>
          <TextField
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder="Enter username (3-20 characters)"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
              endAdornment: checkingUsername ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : usernameAvailable === true ? (
                <InputAdornment position="end">
                  <CheckCircleIcon sx={{ color: '#10B981' }} />
                </InputAdornment>
              ) : usernameAvailable === false ? (
                <InputAdornment position="end">
                  <CloseIcon sx={{ color: '#EF4444' }} />
                </InputAdornment>
              ) : null
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
                '&:hover': { bgcolor: '#F3F4F6' },
                '&.Mui-focused': { bgcolor: 'white' }
              }
            }}
          />
          <Typography variant="caption" color="#6B7280" sx={{ mt: 0.5, display: 'block' }}>
            Username allows user to login without email. Letters, numbers, underscore, and dash only.
          </Typography>
        </Box>

        {/* Password Field */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>
            New Password
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter new password (min 6 characters)"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
                '&:hover': { bgcolor: '#F3F4F6' },
                '&.Mui-focused': { bgcolor: 'white' }
              }
            }}
          />
        </Box>

        {/* Confirm Password Field */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 1 }}>
            Confirm Password
          </Typography>
          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
                '&:hover': { bgcolor: '#F3F4F6' },
                '&.Mui-focused': { bgcolor: 'white' }
              }
            }}
          />
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#6B7280',
            '&:hover': { bgcolor: '#F3F4F6' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || (usernameAvailable === false)}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)'
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Save Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
